import { z } from 'zod';

/**
 * `defer-llm-tool-use` — wire contracts for the LLM function /
 * tool-calling surface.
 *
 * Goal: give callers a single provider-agnostic shape to request a
 * tool call. The intent-engine adapter translates this shape into
 * whatever each provider expects (OpenAI / Groq `tools` array,
 * Anthropic `tools` + `tool_choice`), and translates the response
 * back into a discriminated `LlmToolCallResponse` so the consumer
 * can branch on `type === 'tool_call'` vs `type === 'text'`.
 *
 * Why a separate endpoint (vs. shoehorning tools into
 * `POST /api/intent/parse`):
 *
 *   - Parse is opinionated about the `TestIntent` JSON shape. Tool
 *     use is generic — the operator picks the tool schema.
 *   - Parse always returns text (TestIntent JSON). Tool use can
 *     respond with a tool call OR with plain text, depending on
 *     whether the model decided to call.
 *   - Different feature key in `AiFeature` → different telemetry
 *     bucket (`feature='toolUse'` in `llm_calls`).
 *
 * v1 scope:
 *
 *   - Single user message (the operator-side prompt) + optional
 *     system message. Multi-turn chat is deferred to v2.
 *   - Up to 16 tools per request, each tool name in
 *     `^[a-zA-Z0-9_-]{1,64}$`, parameters expressed as JSON Schema.
 *   - `toolChoice` of `'auto'` (default), `'required'`, `'none'`,
 *     or a specific `{ name }` pin.
 *   - Response is a discriminated union: tool call → `toolName` +
 *     `arguments` (raw string from provider, parsed JSON if it
 *     decodes), OR a plain text response when the model declined
 *     to call.
 *
 * Deferred (v1.1+):
 *
 *   - Multi-turn (chat-style) tool-call loops with the result of
 *     a tool execution fed back in.
 *   - Parallel tool calls (most providers return them already, but
 *     the v1 response shape only carries one for simplicity).
 *   - Streaming tool-call deltas.
 */

/**
 * A single tool definition. `parameters` is JSON Schema — we keep
 * it as `Record<string, unknown>` rather than re-deriving the full
 * JSON-schema Zod since the LLM contract is "anything the provider
 * accepts". Empty `properties` is allowed (no-arg tools).
 */
export const LlmToolDefinitionSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-zA-Z0-9_-]+$/, 'tool name must match [a-zA-Z0-9_-]+'),
  description: z.string().max(2000).default(''),
  parameters: z.record(z.unknown()).default({}),
});
export type LlmToolDefinition = z.infer<typeof LlmToolDefinitionSchema>;

/**
 * `toolChoice` — how the engine constrains the model.
 *   - `'auto'`     — model decides (default).
 *   - `'required'` — model must call exactly one tool.
 *   - `'none'`     — model must respond with text.
 *   - `{ name }`   — pin to a specific tool.
 */
export const LlmToolChoiceSchema = z.union([
  z.literal('auto'),
  z.literal('required'),
  z.literal('none'),
  z.object({ name: z.string().min(1).max(64) }),
]);
export type LlmToolChoice = z.infer<typeof LlmToolChoiceSchema>;

export const LlmToolCallRequestSchema = z.object({
  /** Optional system prompt. */
  system: z.string().max(8000).optional(),
  /** Required user message. */
  prompt: z.string().min(1).max(16000),
  /** 1 – 16 tools the model may call. */
  tools: z.array(LlmToolDefinitionSchema).min(1).max(16),
  toolChoice: LlmToolChoiceSchema.default('auto'),
  /** Override the routed provider's default model. */
  model: z.string().max(120).optional(),
  /** Sampling temperature. Defaults to 0.1 for deterministic calls. */
  temperature: z.number().min(0).max(2).optional(),
});
export type LlmToolCallRequest = z.infer<typeof LlmToolCallRequestSchema>;

/**
 * Response shape — discriminated union over `type`.
 *
 *   - `'tool_call'` — the model chose to call a tool. `arguments`
 *     is the raw string the model returned (most providers return
 *     a JSON string but some return non-JSON when the prompt is
 *     malformed). `parsedArguments` is the JSON-decoded object if
 *     the string parses, `null` otherwise.
 *   - `'text'`      — the model returned a plain-text response,
 *     either because `toolChoice` was `'none'` or because the
 *     model declined to call despite tools being available.
 */
export const LlmToolCallToolBranchSchema = z.object({
  type: z.literal('tool_call'),
  toolName: z.string().min(1),
  arguments: z.string(),
  parsedArguments: z.record(z.unknown()).nullable(),
});
export type LlmToolCallToolBranch = z.infer<typeof LlmToolCallToolBranchSchema>;

export const LlmToolCallTextBranchSchema = z.object({
  type: z.literal('text'),
  text: z.string(),
});
export type LlmToolCallTextBranch = z.infer<typeof LlmToolCallTextBranchSchema>;

export const LlmToolCallResponseSchema = z.object({
  /** Provider that handled the call (e.g. `'openai'`, `'anthropic'`). */
  provider: z.string().min(1),
  /** Concrete model id used (e.g. `'gpt-4o-mini'`). */
  model: z.string().min(1),
  result: z.discriminatedUnion('type', [LlmToolCallToolBranchSchema, LlmToolCallTextBranchSchema]),
  usage: z
    .object({
      inputTokens: z.number().int().nonnegative(),
      outputTokens: z.number().int().nonnegative(),
      totalTokens: z.number().int().nonnegative(),
    })
    .optional(),
});
export type LlmToolCallResponse = z.infer<typeof LlmToolCallResponseSchema>;

/**
 * Safe-parse the raw `arguments` string into JSON. Returns `null`
 * if the string isn't a JSON object (number, array, bad shape …).
 *
 * Exported as a helper so callers and tests can stay aligned on
 * what counts as a successful argument decode.
 */
export function tryParseToolArguments(raw: string): Record<string, unknown> | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}
